import { ClientError, GraphQLClient } from 'graphql-request';
import { Observable, defer, take } from 'rxjs';
import { environment } from '../environment';
import { authService } from '../shared/services/auth/auth.service';
import {
  createSessionExpiredError,
  expireSession,
  refreshIdTokenOnce,
} from '../shared/services/auth/sessionExpiry.helper';

const appEnv = import.meta.env.VITE_REACT_APP_ENV as keyof typeof environment;
const client = new GraphQLClient(environment[appEnv].api);

function is401(err: unknown): boolean {
  return err instanceof ClientError && err.response.status === 401;
}

function buildHeaders(token: string, alternativeGuid: string): Record<string, string> {
  return {
    Authorization: encodeURIComponent(token),
    ...(environment[appEnv].alternativeGuid && { alternativeguid: alternativeGuid || '' }),
  };
}

/**
 * Execute a GraphQL operation with automatic recovery from expired sessions.
 *
 * Flow:
 *   1. Read the session ONCE — the idToken and the company GUID come from the
 *      same token read (`requestIdentity()`, WA-3947), so one request never
 *      starts two Cognito reads. Amplify answers from its token store; a
 *      near-expiry token commonly resolves to `''`.
 *   2. If the token is empty, force-refresh BEFORE sending — avoids the
 *      guaranteed-401 round-trip of submitting `Authorization: ''`.
 *   3. Send the request. On a transport-level 401, force-refresh once and
 *      retry. If the refresh itself fails (refresh token dead / server-side
 *      logout), that's a genuine session end: mark the error, expire the local
 *      session, and redirect to login before rethrowing for the caller's error
 *      UX. But if a *freshly refreshed* token is still rejected with 401, a
 *      valid token is being refused — a real backend/authorizer bug — so we do
 *      NOT mark or redirect (a fresh login would loop straight back into the
 *      same rejection); the error flows through to Sentry as a real signal.
 *   4. Non-401 errors pass through untouched.
 *
 * See WA-3514 / WA-3515: previously a stale token would send `''` and the
 * resulting 401 fed Sentry, which auto-created Jira tickets for what is
 * really just an end-of-session lifecycle event.
 */
export const gqlClient = (gql: string, variables: Record<string, any> = {}): Observable<any> => {
  return defer(() => authService.sub.requestIdentity().pipe(take(1))).pipe(
    take(1),
    // Use a Promise-returning function inside switchMap so the
    // refresh-and-retry control flow stays linear and readable.

    (source$) =>
      new Observable<any>((subscriber) => {
        const sub = source$.subscribe({
          next: async ({ idToken: initialToken, companyGuid: alternativeGuid }) => {
            try {
              const effectiveToken = initialToken || (await refreshIdTokenOnce());
              if (!effectiveToken) {
                // Cognito session is irrecoverable — surface a clean error
                // and skip Sentry; the auth flow will redirect to login.
                subscriber.error(createSessionExpiredError());
                return;
              }

              try {
                const data = await client.request(
                  gql,
                  variables,
                  buildHeaders(effectiveToken, alternativeGuid),
                );
                subscriber.next(data);
                subscriber.complete();
                return;
              } catch (err) {
                if (!is401(err)) {
                  subscriber.error(err);
                  return;
                }
                // Single retry with a freshly refreshed token.
                const refreshed = await refreshIdTokenOnce();
                if (!refreshed) {
                  expireSession(err);
                  subscriber.error(err);
                  return;
                }
                try {
                  const data = await client.request(
                    gql,
                    variables,
                    buildHeaders(refreshed, alternativeGuid),
                  );
                  subscriber.next(data);
                  subscriber.complete();
                } catch (retryErr) {
                  // A freshly refreshed token was still rejected with 401: a
                  // valid token is being refused, which is a backend/authorizer
                  // bug, not an expired session. Do NOT mark it expected and do
                  // NOT redirect (re-login would hit the same rejection and
                  // loop) — let it reach Sentry as the "something is really
                  // wrong" signal.
                  subscriber.error(retryErr);
                }
              }
            } catch (outerErr) {
              subscriber.error(outerErr);
            }
          },
          error: (err) => subscriber.error(err),
        });
        return () => sub.unsubscribe();
      }),
  );
};
